import {
  createAppKit,
  type AppKit,
  type UseAppKitAccountReturn,
} from "@reown/appkit";
import { Ethers5Adapter } from "@reown/appkit-adapter-ethers5";
import { mainnet } from "@reown/appkit/networks";
import { ethers } from "ethers";
import type { Wallet } from "@/app/types/wallet";
import { publicEnvConfig } from "../config/config";
import { MetamaskErrorCodes } from "../services/enum/metamask-error-codes";
import { WalletProviderType } from "../services/enum/wallet-provider-type";
import {
  clearWalletData,
  getStoredWalletData,
  storeWalletData,
} from "./walletUtils";

type Eip1193Provider = ethers.providers.ExternalProvider & {
  request<T = unknown>(args: {
    method: string;
    params?: readonly unknown[] | object;
  }): Promise<T>;
  enable?: () => Promise<void>;
};

const EVM_NAMESPACE = "eip155";
const MODAL_CLOSE_SETTLE_DELAY_MS = 500;
const PROVIDER_READY_TIMEOUT_MS = 5000;
const PROVIDER_READY_POLL_INTERVAL_MS = 150;
const FEATURED_WALLET_IDS = [
  "8a0ee50d1f22f6651afcae7eb4253e52a3310b90af5daef78a8c4929a9bb99d4",
  "f2436c67184f158d1beda5df53298ee84abfc367581e4505134b5bcf5f46697d",
  "971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709",
  "fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa",
  "a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393",
];

let appKit: AppKit | undefined;
let connectPromise: Promise<string | false> | undefined;

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const getAppKit = () => {
  if (appKit) {
    return appKit;
  }

  if (typeof window === "undefined") {
    throw new Error("WalletConnect can only be initialized in the browser.");
  }

  const metadata = {
    name: "Sleap.io",
    description:
      "First native Web3 Hotel Booking Platform using Blockchain, " +
      "the revolutionary blockchain-based hotel booking platform. Dive into a " +
      "curated selection of hotel deals, all with special wallet rates, bringing " +
      "personalized offers right to your digital doorstep. Experience the future of " +
      "travel with cryptocurrency payments and unmatched convenience.",
    url: window.location.origin,
    icons: [`${window.location.origin}/assets/v2/images/sleap-logo.png`],
  };

  appKit = createAppKit({
    adapters: [new Ethers5Adapter()],
    networks: [mainnet],
    defaultNetwork: mainnet,
    metadata,
    projectId: publicEnvConfig.walletConnectProjectID,
    coinbasePreference: "eoaOnly",
    enableEIP6963: true,
    featuredWalletIds: FEATURED_WALLET_IDS,
    features: {
      analytics: true,
      email: false,
      socials: false,
    },
  });

  return appKit;
};

export class WalletConnect implements Wallet {
  activeProvider: undefined | ethers.providers.Web3Provider;

  async connect() {
    const modal = getAppKit();
    const connectedAddress = this.getConnectedAddress(modal);

    if (connectedAddress) {
      const provider = await this.waitForProviderAccount(
        modal,
        connectedAddress
      );
      return this.persistConnectedWallet(modal, connectedAddress, provider);
    }

    if (!connectPromise) {
      connectPromise = this.openAndWaitForConnection(modal).finally(() => {
        connectPromise = undefined;
      });
    }

    return connectPromise;
  }

  async disconnect(): Promise<void> {
    try {
      await getAppKit().disconnect(EVM_NAMESPACE);
      clearWalletData();
    } catch (error) {
      console.warn(error);
    }
  }

  async signTransaction(
    walletId: string,
    challengeMessage: string
  ): Promise<string> {
    let signature;
    if (this.activeProvider) {
      try {
        const signer = this.activeProvider.getSigner(walletId);
        signature = await signer.signMessage(challengeMessage);
        return Promise.resolve(signature);
      } catch (e) {
        return Promise.reject("unable to sign Message");
      }
    } else {
      const modal = getAppKit();
      const provider = await this.waitForWalletProvider(modal);
      if (!provider) {
        return Promise.reject("No wallet provider found");
      }

      if (provider.enable) {
        await provider.enable();
      }

      const web3Provider = new ethers.providers.Web3Provider(provider, "any");
      const signer = web3Provider.getSigner();
      signature = await signer.signMessage(challengeMessage);
      return Promise.resolve(signature);
    }
  }

  async isConnected() {
    const modal = getAppKit();
    const walletProviderType = WalletProviderType.WALLET_CONNECT;
    const { walletAddress } = getStoredWalletData();

    if (!walletAddress) {
      return "";
    }

    const provider = await this.waitForWalletProvider(modal);
    if (!provider) {
      return "";
    }

    const web3Provider = new ethers.providers.Web3Provider(provider, "any");
    const accounts = await this.getProviderAccounts(provider);
    const connectedAddress = this.getConnectedAddress(modal);
    const restoredAccount = connectedAddress ? [connectedAddress] : [];
    const resolvedAccounts = accounts.length > 0 ? accounts : restoredAccount;

    if (resolvedAccounts.length > 0) {
      this.listenToEvents(web3Provider);
      return {
        accounts: resolvedAccounts,
        providerType: walletProviderType,
      };
    }

    return "";
  }

  walletConnectProvider() {
    return getAppKit();
  }

  async connectWithWalletConnect() {
    const modal = getAppKit();
    const provider = await this.waitForWalletProvider(modal);

    if (provider) {
      try {
        const value =
          this.getConnectedAddress(modal) ??
          (await this.requestAccountFromProvider(provider));

        if (!value) {
          return false;
        }

        return this.persistConnectedWallet(modal, value, provider);
      } catch (e: any) {
        const error = this.handleWalletError(e.code);
        Promise.reject(error);
        return false;
      }
    } else {
      const error = this.handleWalletError(
        MetamaskErrorCodes.NO_METAMASK_PLUGIN
      );
      Promise.reject(error);
      return false;
    }
  }

  async getAddress() {
    return "";
  }

  async listenToEvents(provider: ethers.providers.Web3Provider) {
    if (this.activeProvider) {
      // Remove existing listeners
      this.activeProvider?.removeAllListeners("accountsChanged");
      this.activeProvider?.removeAllListeners("disconnect");
    }

    // Set new provider and add listeners
    this.activeProvider = provider;
    this.activeProvider?.addListener(
      "accountsChanged",
      this.onAccountsChanged.bind(this)
    );
    this.activeProvider?.addListener(
      "disconnect",
      this.persistLastUsedWallet.bind(this)
    );
  }

  private getWalletProvider(modal = getAppKit()) {
    return (
      modal.getProvider<Eip1193Provider>(EVM_NAMESPACE) ??
      (modal.getWalletProvider() as Eip1193Provider | undefined)
    );
  }

  private getConnectedAddress(modal: AppKit) {
    return (
      modal.getAddress(EVM_NAMESPACE) ??
      modal.getAccount(EVM_NAMESPACE)?.address
    );
  }

  private async waitForWalletProvider(modal: AppKit) {
    const startedAt = Date.now();
    void modal.ready().catch(() => undefined);

    while (Date.now() - startedAt < PROVIDER_READY_TIMEOUT_MS) {
      const provider = this.getWalletProvider(modal);
      if (provider) {
        return provider;
      }

      await wait(PROVIDER_READY_POLL_INTERVAL_MS);
    }

    return this.getWalletProvider(modal);
  }

  private async openAndWaitForConnection(modal: AppKit) {
    return new Promise<string | false>((resolve, reject) => {
      let settled = false;
      let modalWasOpened = false;
      let closeTimer: ReturnType<typeof setTimeout> | undefined;
      let unsubscribeAccount = () => {};
      let unsubscribeState = () => {};

      const cleanup = () => {
        if (closeTimer) {
          clearTimeout(closeTimer);
        }
        unsubscribeAccount();
        unsubscribeState();
      };

      const resolveConnectedWallet = async (address?: string) => {
        if (settled || !address) {
          return;
        }

        settled = true;
        cleanup();
        try {
          const provider = await this.waitForProviderAccount(modal, address);
          resolve(this.persistConnectedWallet(modal, address, provider));
        } catch (error) {
          this.resetWalletConnectRequest(modal);
          reject(error);
        }
      };

      const rejectConnection = (error: unknown) => {
        if (settled) {
          return;
        }

        settled = true;
        this.resetWalletConnectRequest(modal);
        cleanup();

        reject(error);
      };

      const resolveWithoutConnection = () => {
        if (settled) {
          return;
        }

        settled = true;
        this.resetWalletConnectRequest(modal);
        cleanup();
        resolve(false);
      };

      unsubscribeAccount = modal.subscribeAccount(
        (account: UseAppKitAccountReturn) => {
          if (account.isConnected && account.address) {
            resolveConnectedWallet(account.address).catch(rejectConnection);
          }
        },
        EVM_NAMESPACE
      );

      unsubscribeState = modal.subscribeState((state) => {
        if (state.open) {
          modalWasOpened = true;
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = undefined;
          }
          return;
        }

        if (modalWasOpened) {
          closeTimer = setTimeout(() => {
            if (settled) {
              return;
            }

            const address = this.getConnectedAddress(modal);

            if (address) {
              resolveConnectedWallet(address).catch(rejectConnection);
            } else {
              resolveWithoutConnection();
            }
          }, MODAL_CLOSE_SETTLE_DELAY_MS);
        }
      });

      const existingAddress = this.getConnectedAddress(modal);
      if (existingAddress) {
        resolveConnectedWallet(existingAddress).catch(rejectConnection);
        return;
      }

      this.resetWalletConnectRequest(modal);

      modal
        .open({
          view: "Connect",
          namespace: EVM_NAMESPACE,
        })
        .catch(rejectConnection);
    });
  }

  private resetWalletConnectRequest(modal: AppKit) {
    try {
      modal.resetWcConnection();
    } catch (error) {
      console.warn(error);
    }
  }

  private async waitForProviderAccount(modal: AppKit, address: string) {
    const startedAt = Date.now();
    void modal.ready().catch(() => undefined);

    while (Date.now() - startedAt < PROVIDER_READY_TIMEOUT_MS) {
      const provider = this.getWalletProvider(modal);
      if (provider) {
        const accounts = await this.getProviderAccounts(provider);
        const connectedAddress = this.getConnectedAddress(modal);

        if (
          accounts.some(
            (account) => account.toLowerCase() === address.toLowerCase()
          ) ||
          connectedAddress?.toLowerCase() === address.toLowerCase()
        ) {
          return provider;
        }
      }

      await wait(PROVIDER_READY_POLL_INTERVAL_MS);
    }

    return this.getWalletProvider(modal);
  }

  private async getProviderAccounts(provider: Eip1193Provider) {
    return provider
      .request<string[]>({ method: "eth_accounts" })
      .catch(() => []);
  }

  private async requestAccountFromProvider(provider: Eip1193Provider) {
    const accounts = await provider
      .request<string[]>({
        method: "eth_accounts",
      })
      .catch(() => []);

    if (accounts?.[0]) {
      return accounts[0];
    }

    const requestedAccounts = await provider.request<string[]>({
      method: "eth_requestAccounts",
    });

    return requestedAccounts?.[0];
  }

  private persistConnectedWallet(
    modal: AppKit,
    walletAddress: string,
    provider = this.getWalletProvider(modal)
  ) {
    if (!provider) {
      return false;
    }

    const web3Provider = new ethers.providers.Web3Provider(provider, "any");
    this.listenToEvents(web3Provider);
    return this.persistLastUsedWallet(
      WalletProviderType.WALLET_CONNECT,
      walletAddress
    );
  }

  onAccountsChanged(res: string[]) {
    const walletAddress = res?.[0] ?? null;
    if (!walletAddress) {
      this.persistLastUsedWallet();
    }
  }

  handleWalletError(errorCode: string): string {
    let error = "";
    switch (errorCode) {
      case MetamaskErrorCodes.NO_METAMASK_PLUGIN: {
        error = "No metamask plugin found.";
        break;
      }
      case "ACTION_REJECTED": {
        error = "You rejected the transaction.";
        break;
      }
      case MetamaskErrorCodes.NO_WALLET_CONNECTED: {
        error = "You rejected the transaction.";
        break;
      }
      case MetamaskErrorCodes.WALLET_DISCONNECTED: {
        error = "Wallet has been disconnected.";
        break;
      }
      default: {
        error = "Unknown error while connecting your metamask wallet";
      }
    }
    return error;
  }

  persistLastUsedWallet = (
    walletProviderType?: WalletProviderType,
    walletAddress?: string
  ) => {
    if (!walletProviderType || !walletAddress) {
      clearWalletData();
      return "";
    } else {
      const walletId = getStoredWalletData().walletAddress;
      const walletType = getStoredWalletData().walletProviderType;
      if (walletId === walletAddress && walletType === walletProviderType) {
        return walletId;
      }

      storeWalletData(walletProviderType, walletAddress!);

      return walletAddress;
    }
  };

  getConnectedWallet(): WalletProviderType {
    return WalletProviderType.WALLET_CONNECT;
  }
}
